import * as React from 'react';

import { ErrorBoundary } from 'bernie-view';

import { BrandResultListing } from '@shared-ui/sponsored-content-brand-result-listing';
import { Experiment, ExperimentControl, ExperimentVariant } from '@shared-ui/experiment-context';

import { SearchCriteriaContext } from 'stores/contexts';
import { SearchCriteriaModel } from 'stores/models/search-criteria-model';

import { UitkSpacing } from 'uitk-react-spacing';

import { DateUtils } from '../../utils/date-utils';

import { SponsoredContentLocationResolver, SponsoredContentTargetingInput } from '__generated__/api';

interface MesoBrandResultListingProps {
  uci: string;
  className?: string;
  errorBoundaryLabel?: string;
}

export const MesoBrandResultListing: React.FunctionComponent<MesoBrandResultListingProps> = props => {
  const { errorBoundaryLabel, uci, className } = props;
  const searchCriteria: SearchCriteriaModel = React.useContext(SearchCriteriaContext);
  const { regionId, startDate, endDate, adults, children, origin } = searchCriteria;

  const targetingInput: SponsoredContentTargetingInput = {
    adults,
    dateEnd: DateUtils.parseDateString(endDate),
    dateStart: DateUtils.parseDateString(startDate),
    destination: regionId,
    kids: children,
    locationResolver: SponsoredContentLocationResolver.GAIA,
    origin,
  };

  return (
    <Experiment name="kevel_brand_result_listing_feature_gate">
      <ExperimentControl>
        <MesoBrandResultListingGAM className={className} uci={uci} errorBoundaryLabel={errorBoundaryLabel} />
      </ExperimentControl>
      <ExperimentVariant bucket={1}>
        <Experiment name="kevel_brand_result_listing">
          <ExperimentControl>
            <MesoBrandResultListingGAM className={className} uci={`${uci}_CONTROL`} errorBoundaryLabel={errorBoundaryLabel} />
          </ExperimentControl>
          <ExperimentVariant bucket={1}>
            <ErrorBoundary label={errorBoundaryLabel}>
              <UitkSpacing margin={{ blockstart: 'three' }}>
                <li data-stid="meso-native">
                  <BrandResultListing
                    inputs={{
                      sponsoredContentContext: {
                        pageName: 'page.Hotels.Search',
                        variant: 'brl_poc',
                        targeting: targetingInput,
                      },
                    }}
                  />
                </li>
              </UitkSpacing>
            </ErrorBoundary>
          </ExperimentVariant>
          <ExperimentVariant bucket={2}>
            <MesoBrandResultListingGAM className={className} uci={uci} errorBoundaryLabel={errorBoundaryLabel} />
          </ExperimentVariant>
        </Experiment>
      </ExperimentVariant>
    </Experiment>
  );
};

export const MesoBrandResultListingGAM: React.FunctionComponent<MesoBrandResultListingProps> = props => {
  const [adServed, setAdServed] = React.useState(false);

  const adRef = React.useRef(null);

  React.useEffect(() => {
    const node = adRef.current;

    if (node) {
      node.addEventListener('mesoAd', e => {
        if (e && e.detail) {
          if (e.detail.code === 'AD-LOAD-PASS') {
            setAdServed(true);
          }
          if (e.detail.code === 'AD-LOAD-EMPTY') {
            setAdServed(false);
          }
        }
      });
    }
  }, []);

  const { errorBoundaryLabel, uci, className } = props;

  return (
    <ErrorBoundary label={errorBoundaryLabel}>
      <li data-stid="meso-native" className={adServed ? className : ''}>
        <meso-native ref={adRef} uci={uci} nativetype="tile" isfluid={true} adslot={uci} data-testid="meso-native" />
      </li>
    </ErrorBoundary>
  );
};

MesoBrandResultListing.displayName = 'MesoBrandResultListing';
MesoBrandResultListing.defaultProps = {
  errorBoundaryLabel: 'hotel.meso',
};
